import React, { useEffect, useMemo, useState } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { QRCodeCanvas } from 'qrcode.react';
import {
    FiMaximize, FiUploadCloud, FiCheckCircle, FiAlertTriangle,
    FiCopy, FiDownload, FiPrinter, FiWifi, FiTag, FiSmartphone, FiXCircle,
} from 'react-icons/fi';

import { db } from '../firebase';
import { uploadFileToWasabiAtKey } from '../utils/wasabiUploader';
import { readApkSignatureChecksum, readApkFileChecksum } from '../utils/apkSignature';

// The provisioning APK lives at ONE fixed key. Every QR code ever printed points here, so new agent
// releases overwrite this object instead of landing next to it under a new timestamped name.
const APK_KEY = 'provisioning/musicdj-mdm.apk';
const ADMIN_COMPONENT = 'rs.musicdj.mdm/rs.maketv.monitoringservice.receivers.MyDeviceAdminReceiver';
const CONFIG_PATH = 'mdm/provisioning/qr';

const SECURITY_TYPES = ['WPA', 'WEP', 'NONE'];

export default function ProvisioningPage() {
    const [cfg, setCfg] = useState({
        apkUrl: '', checksum: '', sha256Hex: '', uploadedAt: null, apkName: '',
        wifiSsid: '', wifiPassword: '', wifiSecurity: 'WPA',
        enrollmentToken: '', customer: '',
        leaveSystemApps: true, skipEncryption: true,
    });
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState('');

    useEffect(() => {
        return onValue(ref(db, CONFIG_PATH), (snap) => {
            const v = snap.val() || {};
            setCfg((c) => ({ ...c, ...v }));
            setLoaded(true);

            // Persist the defaults the operator never touched. Until now a field left alone (the
            // security dropdown is the usual one) simply had no value in the database: the QR was
            // still right, because it is built from React state, but anything else reading the stored
            // config would have seen a hole where "WPA" was implied. Write them once so what is
            // stored is what is used.
            const missing = {};
            if (v.wifiSecurity === undefined) missing.wifiSecurity = 'WPA';
            if (v.leaveSystemApps === undefined) missing.leaveSystemApps = true;
            if (v.skipEncryption === undefined) missing.skipEncryption = true;
            if (Object.keys(missing).length) {
                update(ref(db, CONFIG_PATH), missing).catch(() => {});
            }
        });
    }, []);

    const save = async (patch) => {
        setCfg((c) => ({ ...c, ...patch }));
        try {
            await update(ref(db, CONFIG_PATH), patch);
        } catch (e) {
            toast.error('Could not save: ' + e.message);
        }
    };

    // Upload + read the certificate in one step: the checksum MUST describe the APK actually sitting
    // at the URL, so the two are never allowed to drift apart.
    const onApkPicked = async (file) => {
        if (!file) return;
        if (!/\.apk$/i.test(file.name)) { toast.error('That is not an APK.'); return; }
        try {
            setBusy('Reading the signing certificate…');
            const { checksum, sha256Hex } = await readApkSignatureChecksum(file);
            const fileChecksum = await readApkFileChecksum(file);

            setBusy('Uploading to Wasabi…');
            const { publicUrl } = await uploadFileToWasabiAtKey(file, APK_KEY);

            await save({
                apkUrl: publicUrl, checksum, sha256Hex, fileChecksum,
                apkName: file.name, uploadedAt: Date.now(),
            });
            toast.success('APK published — the QR code is ready to print.');
        } catch (e) {
            toast.error(e.message || 'Upload failed');
        } finally {
            setBusy('');
        }
    };

    // The JSON the setup wizard reads after six taps on the welcome screen.
    const qrPayload = useMemo(() => {
        const o = {
            'android.app.extra.PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME': ADMIN_COMPONENT,
            'android.app.extra.PROVISIONING_DEVICE_ADMIN_SIGNATURE_CHECKSUM': cfg.checksum || '',
            'android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION': cfg.apkUrl || '',
        };
        // Optional second check on the APK itself. Not all devices honour the signature checksum,
        // and one that does not fails with the same blank error as everything else - so this is here
        // to be switched on while narrowing a failure down. It pins the exact file rather than the
        // key, so the QR must be regenerated after every agent release while it is on.
        if (cfg.pinApkFile && cfg.fileChecksum) {
            o['android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_CHECKSUM'] = cfg.fileChecksum;
        }
        if (cfg.wifiSsid) {
            o['android.app.extra.PROVISIONING_WIFI_SSID'] = cfg.wifiSsid;
            o['android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE'] = cfg.wifiSecurity;
            if (cfg.wifiSecurity !== 'NONE' && cfg.wifiPassword) {
                o['android.app.extra.PROVISIONING_WIFI_PASSWORD'] = cfg.wifiPassword;
            }
        }
        // Without this, provisioning disables every non-required system app - including the launcher
        // and Play services, which the agent and the player both need.
        if (cfg.leaveSystemApps) o['android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED'] = true;
        if (cfg.skipEncryption) o['android.app.extra.PROVISIONING_SKIP_ENCRYPTION'] = true;

        // Handed to MyDeviceAdminReceiver.onProfileProvisioningComplete(): this is how a device knows
        // whose it is the moment it finishes, instead of us matching it up afterwards.
        const extras = {};
        if (cfg.enrollmentToken) extras.enrollment_token = cfg.enrollmentToken;
        if (cfg.customer) extras.customer = cfg.customer;
        if (Object.keys(extras).length) {
            o['android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE'] = extras;
        }
        return o;
    }, [cfg]);

    const ready = Boolean(cfg.apkUrl && cfg.checksum);

    const copyJson = () => {
        navigator.clipboard.writeText(JSON.stringify(qrPayload, null, 2));
        toast.info('QR JSON copied');
    };

    const downloadPng = () => {
        const canvas = document.querySelector('#provisioning-qr canvas');
        if (!canvas) return;
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = 'musicdj-enrollment-qr.png';
        a.click();
    };

    if (!loaded) {
        return (
            <div className="page-shell">
                <p className="text-sm text-gray-400">Loading…</p>
            </div>
        );
    }

    return (
        <div className="page-shell">
            <ToastContainer position="top-right" autoClose={3000} theme="dark" />

            {/* ---- page header ------------------------------------------------ */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-white inline-flex items-center gap-2">
                        <FiMaximize className="text-brand-400" /> Device Provisioning
                    </h1>
                    <p className="text-sm text-gray-400 mt-1">
                        Enroll a certified device (Lenovo, Hisense, Philips) by scanning one QR code during
                        setup — no cable, no computer.
                    </p>
                </div>
                {ready && (
                    <div className="flex gap-2 self-start sm:self-auto">
                        <button onClick={downloadPng} className="btn-primary"><FiDownload /> Download PNG</button>
                        <button onClick={() => window.print()} className="btn-secondary"><FiPrinter /> Print</button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {/* ================= left column ================= */}
                <div className="space-y-5">

                    <section className="card-chrome">
                        <CardTitle icon={<FiSmartphone />} step="1" title="Agent APK"
                                   subtitle="Generic release of the Monitoring app (rs.musicdj.mdm)" />

                        <p className="text-xs text-gray-400 mb-4">
                            Published to one fixed address, so QR codes printed today keep working after the
                            next release — as long as the signing key does not change.
                        </p>

                        <label className={`flex items-center justify-center gap-2 px-4 py-6 rounded-lg border border-dashed
                            ${busy ? 'border-white/[0.08] text-gray-500 cursor-wait'
                                   : 'border-white/[0.15] text-gray-300 hover:border-brand/40 hover:text-white cursor-pointer'}
                            transition-colors`}>
                            <FiUploadCloud />
                            <span className="text-sm">{busy || 'Choose app-generic-release.apk'}</span>
                            <input type="file" accept=".apk" className="hidden" disabled={!!busy}
                                   onChange={(e) => onApkPicked(e.target.files?.[0])} />
                        </label>

                        {cfg.apkUrl && (
                            <div className="card-inset mt-4 p-3 space-y-2">
                                <Row k="File" v={cfg.apkName || '—'} />
                                <Row k="Published" v={cfg.uploadedAt ? new Date(cfg.uploadedAt).toLocaleString('en-GB') : '—'} />
                                <Row k="URL" v={cfg.apkUrl} mono />
                                <Row k="Signing cert (SHA-256)" v={cfg.sha256Hex} mono />
                                <Row k="QR checksum" v={cfg.checksum} mono />
                                <p className="text-[11px] text-gray-500 pt-1 border-t border-white/[0.04]">
                                    Verify with <code className="text-gray-400">apksigner verify --print-certs</code> —
                                    the hex above must match “certificate SHA-256 digest”.
                                </p>
                            </div>
                        )}
                    </section>

                    <section className="card-chrome">
                        <CardTitle icon={<FiWifi />} step="2" title="Wi-Fi"
                                   subtitle="The device joins this network on its own to download the agent" />

                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <Label>SSID</Label>
                                <input className="input-field" value={cfg.wifiSsid}
                                       onChange={(e) => save({ wifiSsid: e.target.value })}
                                       placeholder="Leave empty to pick a network by hand during setup" />
                            </div>
                            <div>
                                <Label>Security</Label>
                                <select className="input-field" value={cfg.wifiSecurity}
                                        onChange={(e) => save({ wifiSecurity: e.target.value })}>
                                    {SECURITY_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label>Password</Label>
                                <input className="input-field" type="text" value={cfg.wifiPassword}
                                       disabled={cfg.wifiSecurity === 'NONE'}
                                       onChange={(e) => save({ wifiPassword: e.target.value })} />
                            </div>
                        </div>

                        <p className="text-xs text-amber-400/90 mt-3 flex gap-2">
                            <FiAlertTriangle className="mt-0.5 shrink-0" />
                            The QR carries this password in clear text — treat a printed code like a key to
                            that network.
                        </p>
                    </section>

                    <section className="card-chrome">
                        <CardTitle icon={<FiTag />} step="3" title="Who the device belongs to"
                                   subtitle="Passed to the agent the moment provisioning finishes" />

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Enrollment token</Label>
                                <input className="input-field" value={cfg.enrollmentToken}
                                       onChange={(e) => save({ enrollmentToken: e.target.value })}
                                       placeholder="optional" />
                            </div>
                            <div>
                                <Label>Customer</Label>
                                <input className="input-field" value={cfg.customer}
                                       onChange={(e) => save({ customer: e.target.value })}
                                       placeholder="optional" />
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-4 mt-4">
                            <Check checked={cfg.leaveSystemApps} onChange={(v) => save({ leaveSystemApps: v })}
                                   title="Required: without it, provisioning disables the launcher and Play services">
                                Keep system apps
                            </Check>
                            <Check checked={cfg.skipEncryption} onChange={(v) => save({ skipEncryption: v })}
                                   title="Skips the encryption prompt on devices that are not encrypted yet">
                                Skip encryption
                            </Check>
                            <Check checked={cfg.pinApkFile} onChange={(v) => save({ pinApkFile: v })}
                                   title="Also send PROVISIONING_DEVICE_ADMIN_PACKAGE_CHECKSUM. Pins the exact APK file, so the QR must be regenerated after every agent release - use it only while narrowing down a provisioning failure.">
                                Also pin the APK file
                            </Check>
                        </div>
                    </section>
                </div>

                {/* ================= right column ================= */}
                <div className="space-y-5">

                    <section className="card-chrome">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-base font-semibold text-white">Enrollment QR</h2>
                            {ready ? (
                                <span className="brand-pill inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium">
                                    <FiCheckCircle className="w-3 h-3" /> Ready
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-medium">
                                    <FiAlertTriangle className="w-3 h-3" /> Upload the APK first
                                </span>
                            )}
                        </div>

                        <div id="provisioning-qr" className="flex justify-center bg-white rounded-lg p-6">
                            {ready ? (
                                <QRCodeCanvas value={JSON.stringify(qrPayload)} size={300} level="M" includeMargin />
                            ) : (
                                <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">
                                    No QR yet
                                </div>
                            )}
                        </div>

                        {/* What is actually encoded right now. The QR is rebuilt the moment a field
                            changes - there is no Save step - but without this you cannot tell a
                            regenerated code from the one you were just looking at, which is exactly
                            the doubt you do not want while enrolling a device. */}
                        <div className="card-inset mt-4 p-3 space-y-1.5">
                            <p className="text-[11px] font-semibold text-gray-300 mb-2">This QR contains</p>
                            <Item ok={!!cfg.apkUrl}>
                                Agent APK{cfg.apkName ? ` — ${cfg.apkName}` : ''}
                            </Item>
                            <Item ok={!!cfg.checksum}>Signature check (only our key installs)</Item>
                            <Item ok={!!cfg.wifiSsid}
                                  offText={`No Wi-Fi — the installer picks a network during setup`}>
                                Wi-Fi “{cfg.wifiSsid}” ({cfg.wifiSecurity}){cfg.wifiPassword ? ' + password' : ''}
                            </Item>
                            <Item ok={!!cfg.leaveSystemApps}
                                  offText="System apps NOT kept — launcher and Play will be disabled">
                                Keep system apps
                            </Item>
                            <Item ok={!!cfg.skipEncryption} offText="Skip encryption: off">Skip encryption</Item>
                            <Item ok={!!(cfg.enrollmentToken || cfg.customer)}
                                  offText="No token — the device arrives unlabelled">
                                Bound to {[cfg.customer, cfg.enrollmentToken].filter(Boolean).join(' / ')}
                            </Item>
                        </div>

                        {ready && (
                            <div className="flex gap-2 mt-4">
                                <button onClick={downloadPng} className="btn-secondary text-xs px-3 py-1.5">
                                    <FiDownload /> PNG
                                </button>
                                <button onClick={copyJson} className="btn-secondary text-xs px-3 py-1.5">
                                    <FiCopy /> Copy JSON
                                </button>
                            </div>
                        )}
                    </section>

                    <section className="card-chrome">
                        <h2 className="text-base font-semibold text-white mb-3">On the device</h2>
                        <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside marker:text-brand-400">
                            <li><b className="text-white">Factory reset</b> — an existing Device Owner cannot be removed any other way.</li>
                            <li>On the very first setup screen, <b className="text-white">tap 6 times</b> on the same spot — the QR scanner opens.</li>
                            <li>Scan this code. Wi-Fi, download, install and Device Owner all happen on their own.</li>
                            <li>Wait for the agent to appear on the Devices page.</li>
                        </ol>

                        <div className="card-inset mt-4 p-3">
                            <p className="text-xs font-semibold text-amber-300 mb-2 inline-flex items-center gap-1.5">
                                <FiAlertTriangle className="w-3.5 h-3.5" /> Still has to be done by hand
                            </p>
                            <ul className="text-xs text-gray-400 space-y-2 list-disc list-inside">
                                <li>
                                    <b className="text-gray-300">Accessibility</b> — Settings → Accessibility → MusicDJ → On.
                                    Android lets no app, Device Owner included, switch this on for itself. Without it
                                    there is no screen view, no remote input, no soft kiosk and no Volume-Up×5 PIN.
                                </li>
                                <li>
                                    On Android 13+ a sideloaded app first needs <i>App info → ⋮ → Allow restricted
                                    settings</i>. Whether an app installed <i>by provisioning</i> is exempt is exactly
                                    what the first test should tell us.
                                </li>
                            </ul>
                        </div>
                    </section>

                    <section className="card-chrome">
                        <h2 className="text-base font-semibold text-white mb-3">QR payload</h2>
                        <pre className="card-inset p-3 text-[11px] leading-relaxed text-gray-400 overflow-x-auto whitespace-pre-wrap break-all">
{JSON.stringify(qrPayload, null, 2)}
                        </pre>
                    </section>
                </div>
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------- helpers */

function CardTitle({ icon, step, title, subtitle }) {
    return (
        <div className="flex items-start gap-3 mb-4">
            <div className="brand-icon-badge shrink-0">{icon}</div>
            <div className="min-w-0">
                <h2 className="text-base font-semibold text-white">
                    <span className="text-brand-400 mr-1.5">{step}.</span>{title}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
            </div>
        </div>
    );
}

/** One line of the "this QR contains" list: green when present, grey with the consequence when not. */
function Item({ ok, children, offText }) {
    return (
        <div className={`flex items-start gap-2 text-[11px] ${ok ? 'text-gray-300' : 'text-gray-500'}`}>
            {ok
                ? <FiCheckCircle className="w-3 h-3 mt-0.5 shrink-0 text-green-400" />
                : <FiXCircle className="w-3 h-3 mt-0.5 shrink-0 text-gray-600" />}
            <span>{ok ? children : (offText || children)}</span>
        </div>
    );
}

const Label = ({ children }) => (
    <label className="block text-xs font-medium text-gray-400 mb-1.5">{children}</label>
);

function Row({ k, v, mono }) {
    return (
        <div className="flex gap-3 text-xs">
            <span className="text-gray-500 shrink-0 w-36">{k}</span>
            <span className={`text-gray-300 break-all ${mono ? 'font-mono' : ''}`}>{v}</span>
        </div>
    );
}

function Check({ checked, onChange, children, title }) {
    return (
        <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-gray-300" title={title}>
            <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
                   className="accent-brand w-4 h-4" />
            <span>{children}</span>
        </label>
    );
}
